/* eslint-disable @typescript-eslint/no-explicit-any */
import { of } from 'rxjs/observable/of';
import { Observable } from 'rxjs';
import { fromPromise } from 'rxjs/observable/fromPromise';
import { switchMap, catchError } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import { Action } from 'redux-actions';

import {
	GET_REGISTRATION_LINK_STATUS_CALL,
	POST_CREATE_REGISTRATION_EMAIL_USER_CALL,
	POST_CREATE_REGISTRATION_MOBILE_USER_CALL,
	POST_REGISTER_REGISTRATION_LINK_USER_CALL,
	POST_REGISTRATION_LINK_LOGIN_VERIFY_CALL,
} from '@src/store/types/registrationLink';
import {
	getRegistrationLinkStatusDone,
	getRegistrationLinkStatusFail,
	postCreateRegistrationEmailUserDone,
	postCreateRegistrationEmailUserFail,
	postCreateRegistrationMobileUserDone,
	postCreateRegistrationMobileUserFail,
	postRegisterRegistrationLinkUserDone,
	postRegisterRegistrationLinkUserFail,
	postRegistrationLinkLoginVerifyDone,
	postRegistrationLinkLoginVerifyFail,
} from '@src/store/actions/registrationLink';
import {
	getRegistrationLinkStatus,
	postCreateRegistrationEmailUser,
	postCreateRegistrationMobileUser,
	postRegisterRegistrationLinkUser,
	postRegistrationLinkLoginVerify,
} from '@src/services/registrationLink';
import * as actions from '@src/store/actions';
import { ui } from '@src/services';
import { TOAST_TYPE_SUCCESS } from '@src/const/common';
import { reactLocalStorage } from '@src/utils/localStorage';

export const getRegistrationLinkStatusEpic = (
	action$: Observable<Action>,
): Observable<Action> => action$.pipe(
	ofType(GET_REGISTRATION_LINK_STATUS_CALL),
	switchMap(
		({ payload }: any) => fromPromise(getRegistrationLinkStatus(payload.id)).pipe(
			switchMap(res => of(getRegistrationLinkStatusDone({ res }))),
			catchError(error => of(getRegistrationLinkStatusFail({ error }))),
		),
	),
);

export const postCreateRegistrationEmailUserEpic = (
	action$: Observable<Action>,
): Observable<Action> => action$.pipe(
	ofType(POST_CREATE_REGISTRATION_EMAIL_USER_CALL),
	switchMap(
		({ payload: { onSuccess, onError, id, ...form } }: any) => fromPromise(
			postCreateRegistrationEmailUser(id, form),
		).pipe(
			switchMap((res: any) => {
				const resultActions = [postCreateRegistrationEmailUserDone({ res })];

				if (onSuccess) onSuccess(res);
				if (res?.access_token) {
					reactLocalStorage.set('registrationLinkIdAfterLogin', id);
					resultActions.unshift(
						actions.setToken({ token: res.access_token, isJwtToken: true }),
						actions.checkLoginCall({ fromLogin: true }),
					);
				}

				return of(...resultActions);
			}),
			catchError((error: any) => {
				if (onError) onError(error);
				return of(postCreateRegistrationEmailUserFail({ error }));
			}),
		),
	),
);

export const postCreateRegistrationMobileUserEpic = (
	action$: Observable<Action>,
): Observable<Action> => action$.pipe(
	ofType(POST_CREATE_REGISTRATION_MOBILE_USER_CALL),
	switchMap(
		({ payload: { onSuccess, onError, id, ...form } }: any) => fromPromise(
			postCreateRegistrationMobileUser(id, form),
		).pipe(
			switchMap((res: any) => {
				const resultActions = [postCreateRegistrationMobileUserDone({ res })];
				if (onSuccess) onSuccess(res);
				if (res?.access_token) {
					resultActions.unshift(
						actions.setToken({ token: res.access_token, isJwtToken: true }),
						actions.checkLoginCall({ fromLogin: true }),
					);
					reactLocalStorage.set('registrationLinkIdAfterLogin', id);
				}
				return of(...resultActions);
			}),
			catchError((error: any) => {
				if (onError) onError(error);
				return of(postCreateRegistrationMobileUserFail({ error }));
			}),
		),
	),
);

export const postRegisterRegistrationLinkUserEpic = (
	action$: Observable<Action>,
): Observable<Action> => action$.pipe(
	ofType(POST_REGISTER_REGISTRATION_LINK_USER_CALL),
	switchMap(
		({ payload: { onSuccess, onError, id, form } }: any) => fromPromise(
			postRegisterRegistrationLinkUser(id, form),
		).pipe(
			switchMap((res: any) => {
				reactLocalStorage.remove('registrationLinkIdAfterLogin');
				ui.toastShow({
					message: 'common:action:saved',
					type: TOAST_TYPE_SUCCESS,
				});
				if (onSuccess) onSuccess(res);
				return of(
					postRegisterRegistrationLinkUserDone({ res }),
					actions.checkLoginCall({ fromLogin: false }),
				);
			}),
			catchError((error: any) => {
				if (onError) onError(error);
				return of(postRegisterRegistrationLinkUserFail({ error }));
			}),
		),
	),
);

export const postRegistrationLinkLoginVerifyEpic = (
	action$: Observable<Action>,
): Observable<Action> => action$.pipe(
	ofType(POST_REGISTRATION_LINK_LOGIN_VERIFY_CALL),
	switchMap(
		({ payload: { code, onSuccess, onError } }: any) => fromPromise(
			postRegistrationLinkLoginVerify({ code }),
		).pipe(
			switchMap((res: any) => {
				const resultActions: Action[] = [postRegistrationLinkLoginVerifyDone({ res })];
				if (onSuccess) onSuccess(res);
				if (res?.access_token) {
					if (res?.is_registering && res?.registration_link_id) {
						reactLocalStorage.set('registrationLinkIdAfterLogin', res.registration_link_id);
					} else {
						reactLocalStorage.remove('registrationLinkIdAfterLogin');
					}
					resultActions.unshift(
						actions.setToken({ token: res.access_token, isJwtToken: true }),
						actions.checkLoginCall({ fromLogin: true }),
						actions.toastShowCall({
							type: TOAST_TYPE_SUCCESS,
							message: 'share:auth:message:login_success',
						}),
					);
				}
				return of(...resultActions);
			}),
			catchError((error: any) => {
				if (onError) onError(error);
				return of(postRegistrationLinkLoginVerifyFail({ error }));
			}),
		),
	),
);
